import React from "react";

export interface GoldCoinProps {
  /** Size in pixels (width and height). Default is 18 */
  size?: number | string;
  /** Optional extra class name */
  className?: string;
  /** Optional inline styles */
  style?: React.CSSProperties;
}

/**
 * Reusable Temple Dekho Gold Coin component.
 * Features a luxury gold metallic gradient, polished rim, and crisp rupee symbol.
 */
export const GoldCoin: React.FC<GoldCoinProps> = ({
  size = 18,
  className = "",
  style = {},
}) => {
  const numSize = typeof size === "number" ? size : parseInt(size, 10) || 18;
  const strokeWidth = numSize >= 24 ? 1.5 : 1.2;

  return (
    <svg
      width={numSize}
      height={numSize}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`tdGoldCoin ${className}`}
      style={{
        display: "inline-flex",
        verticalAlign: "middle",
        flexShrink: 0,
        filter: "drop-shadow(0 1px 2.5px rgba(217, 119, 6, 0.35))",
        ...style,
      }}
      aria-label="Gold Coin"
      role="img"
    >
      <defs>
        {/* Rich Temple Dekho Metallic Gold Gradient */}
        <linearGradient
          id="tdGoldCoinGrad"
          x1="2"
          y1="2"
          x2="22"
          y2="22"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#FCD34D" />
          <stop offset="35%" stopColor="#F59E0B" />
          <stop offset="100%" stopColor="#D97706" />
        </linearGradient>

        {/* Polished Outer Rim Sheen */}
        <linearGradient
          id="tdGoldCoinRim"
          x1="0"
          y1="0"
          x2="24"
          y2="24"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#FEF3C7" />
          <stop offset="50%" stopColor="#FDE68A" />
          <stop offset="100%" stopColor="#B45309" />
        </linearGradient>
      </defs>

      {/* Outer Coin Body */}
      <circle
        cx="12"
        cy="12"
        r="11"
        fill="url(#tdGoldCoinGrad)"
        stroke="url(#tdGoldCoinRim)"
        strokeWidth={strokeWidth}
      />

      {/* Inner Minting Circle */}
      <circle
        cx="12"
        cy="12"
        r="8.5"
        stroke="#FEF3C7"
        strokeWidth="0.8"
        strokeOpacity="0.65"
        fill="none"
      />

      {/* Crisp White Currency Symbol */}
      <text
        x="12"
        y="15.8"
        textAnchor="middle"
        fill="#FFFFFF"
        fontSize="12.5"
        fontWeight="800"
        fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
        style={{ userSelect: "none" }}
      >
        ₹
      </text>
    </svg>
  );
};

export default GoldCoin;
